"use client";
import React from "react";
import Image from "next/image";
import styles from "./ErpNextIntegrations.module.css";
import { ThreeDMarquee } from "../../../../components/ui/three-d-marquee";
import { erpNextIntegrations as INTEGRATIONS } from "../../data/erp-next";
import { useServiceData } from "../../hooks/useServiceData";

export default function ErpNextIntegrations() {
  const { data: backendPageData } = useServiceData();

  // Find dynamic integration section
  const dynamicSection = backendPageData?.extraSections?.find(
    (s) => s.type === "integrationSection"
  )?.refId;

  // Fallback values
  const displayTag = dynamicSection?.tag || "Real Integrations & Automation Highlights";
  const displayTitle = dynamicSection?.title || "ERPNext Connected with Everything You Already Use";
  const displaySubtitle = dynamicSection?.subtitle || "Streamline workflows, reduce manual work, and unify your business operations effortlessly.";
  const displayTagline = dynamicSection?.highlightText || "⚡ Automate end‑to‑end — from lead capture to invoice generation.";
  
  const getImageUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("data:") || url.startsWith("blob:")) return url;
    
    const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
    
    if (url.includes("/uploads/")) {
      const cleanPath = url.substring(url.indexOf("/uploads/"));
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      return `${apiUrl}${cleanPath}`;
    }
    
    if (url.startsWith("http")) return url;
    return url;
  };

  const displayIntegrations = dynamicSection?.integrations?.map(it => ({
    name: it.name,
    sub: it.description,
    icon: it.icon
  })) || INTEGRATIONS;

  return (
    <section className={`${styles.section} py-5`}>
      <div className="container">
        <header className={styles.head} data-reveal="fade">
          <div className={styles.eyebrow}>
            {displayTag}
          </div>
          <h3 className={styles.title}>
            {displayTitle}
          </h3>
          <p className={styles.subtitle}>
            {displaySubtitle}
          </p>
          <p className={styles.tagline}>
            {displayTagline}
          </p>
        </header>

        {/* 3D Marquee built from integrations */}
        <ThreeDMarquee
          className="bg-transparent"
          centerOffset={64}
          scale={1.1}
          tileHeight={200}
          items={displayIntegrations.map((it, idx) => {
            const iconUrl = typeof it.icon === 'string' ? it.icon : (it.icon?.url || '');
            const isImage = typeof iconUrl === 'string' && (iconUrl.startsWith('http') || iconUrl.includes('/') || iconUrl.includes('.'));
            return (
              <article
                key={`${it.name}-${idx}`}
                className={`${styles.tile} w-full h-full flex items-center`}
              >
                <div className={styles.logo} aria-hidden style={{ position: 'relative' }}>
                  {isImage ? (
                    <Image 
                      src={getImageUrl(iconUrl)} 
                      alt={it.name} 
                      style={{ objectFit: "contain", padding: "8px" }} 
                      fill
                    />
                  ) : (
                    <i className={iconUrl}></i>
                  )}
                  <span className={styles.check}>
                    <i className="fa-solid fa-check"></i>
                  </span>
                </div>
                <div className={styles.meta}>
                  <h5 className={styles.name}>{it.name}</h5>
                  <div className={styles.sub}>{it.sub}</div>
                </div>
              </article>
            );
          })}
        />

        {/* Category cards with benefits */}
        {/* <div className={styles.catGrid} data-reveal="stagger">
          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-cart-shopping"></i></div>
              <h5 className={styles.catTitle}>E‑Commerce</h5>
            </div>
            <div className={styles.catTools}>Shopify / WooCommerce / Magento</div>
            <div className={styles.catDesc}>Sync products, inventory, and orders automatically across your online stores. No double‑entry needed.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-message"></i></div>
              <h5 className={styles.catTitle}>Communication</h5>
            </div>
            <div className={styles.catTools}>Slack / Microsoft Teams / Zoom</div>
            <div className={styles.catDesc}>Receive notifications, updates, and alerts directly where your team collaborates. Keep everyone in the loop instantly.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-credit-card"></i></div>
              <h5 className={styles.catTitle}>Payments</h5>
            </div>
            <div className={styles.catTools}>Razorpay / Stripe / PayPal</div>
            <div className={styles.catDesc}>Auto‑generate invoices, track payments, and reconcile transactions in real‑time.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-envelope"></i></div>
              <h5 className={styles.catTitle}>Email & Calendar</h5>
            </div>
            <div className={styles.catTools}>Google Workspace / Outlook / Office 365</div>
            <div className={styles.catDesc}>Send automated emails, schedule meetings, and sync tasks without leaving ERPNext.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-file-invoice-dollar"></i></div>
              <h5 className={styles.catTitle}>Finance & Accounting</h5>
            </div>
            <div className={styles.catTools}>QuickBooks / Tally / Xero</div>
            <div className={styles.catDesc}>Keep your books accurate and up‑to‑date with automatic journal entries and expense tracking.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-comments"></i></div>
              <h5 className={styles.catTitle}>CRM & Messaging</h5>
            </div>
            <div className={styles.catTools}>WhatsApp Business / Twilio / SMS gateways</div>
            <div className={styles.catDesc}>Send leads, order confirmations, and reminders automatically. Personalized notifications for each customer.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-bullseye"></i></div>
              <h5 className={styles.catTitle}>Marketing Automation</h5>
            </div>
            <div className={styles.catTools}>Mailchimp / HubSpot / ActiveCampaign</div>
            <div className={styles.catDesc}>Trigger campaigns based on customer activity or order status, all synced from ERPNext.</div>
          </article>

          <article className={styles.catCard}>
            <div className={styles.catHead}>
              <div className={styles.catIcon}><i className="fa-solid fa-truck"></i></div>
              <h5 className={styles.catTitle}>Shipping & Logistics</h5>
            </div>
            <div className={styles.catTools}>FedEx / DHL / ShipRocket</div>
            <div className={styles.catDesc}>Automate shipping labels, tracking updates, and delivery notifications.</div>
          </article>
        </div> */}

        {/* Key automation highlights */}
        {/* <div className={styles.highlights} data-reveal="stagger-mix">
          <div className={styles.hiItem}><i className="fa-solid fa-bolt"></i><span>End‑to‑End Workflow Automation – From lead capture → quotation → sales order → invoice → payment.</span></div>
          <div className={styles.hiItem}><i className="fa-solid fa-arrows-rotate"></i><span>Real‑Time Sync Across Platforms – Never update the same data twice.</span></div>
          <div className={styles.hiItem}><i className="fa-solid fa-chart-pie"></i><span>Instant Reporting – All integrated systems feed dashboards automatically.</span></div>
          <div className={styles.hiItem}><i className="fa-solid fa-screwdriver-wrench"></i><span>Custom Workflows – Adapt ERPNext to fit your unique business processes.</span></div>
          <div className={styles.hiItem}><i className="fa-solid fa-circle-check"></i><span>Visual Trust – Hoverable logos, animations, and tooltips show live integration status.</span></div>
          <div className={styles.hiItem}><i className="fa-solid fa-robot"></i><span>Reduce Human Error – Automatic reconciliations, alerts, and reminders ensure nothing slips through.</span></div>
          <div className={styles.hiItem}><i className="fa-solid fa-diagram-project"></i><span>One Platform, Everything Connected – Eliminate app‑hopping and scattered data.</span></div>
        </div> */}
      </div>
    </section>
  );
}
